import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Plus, Star, Trash2, TriangleAlert, X } from 'lucide-react';
import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  DECISION_OTHER_KEY,
  type DecisionImportIssue,
  type DecisionQuestionView,
  type DecisionSheetStatus,
  MAX_DECISION_ASKS,
  MAX_DECISION_OPTIONS,
  MIN_DECISION_OPTIONS,
  type UpsertDecisionQuestionInput,
  splitDecisionKeys,
} from '@apm/contracts';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import {
  addOption,
  draftOf,
  draftSnapshot,
  emptyDraft,
  insertAsk,
  type QuestionDraft,
  questionInputOf,
  removeAsk,
  toggleMultiple,
  toggleRecommended,
} from '../question-draft';
import { InlineText } from './inline-text';
import { ORIGIN_CLASS, PILL } from './labels';

/** Ngừng gõ bao lâu thì tự lưu câu, kể cả khi chưa rời thẻ. */
export const QUESTION_SAVE_DELAY_MS = 2_000;

const ORIGINS = ['documented', 'new'] as const;

/** Nút chỉ hiện khi hover — trên màn cảm ứng (không có hover) và điện thoại thì luôn hiện. */
const HOVER_REVEAL =
  'opacity-0 group-hover/option:opacity-100 focus-visible:opacity-100 max-sm:opacity-100 pointer-coarse:opacity-100';

/**
 * Một câu trong chế độ **Sửa** — APM-DSX-e, plan §11.4.
 *
 * Cùng khung thẻ, cùng thứ tự khối với `DecisionQuestionCard` (mã · tiêu đề ·
 * nhãn nguồn · Tài liệu · Tình huống · Cần chốt · phương án dạng thẻ), nhưng
 * mọi chữ sửa tại chỗ. Không còn hộp thoại.
 *
 * ## Lưu
 *
 * **Một** lệnh `UpsertDecisionQuestion` cho cả câu, khi focus **rời thẻ** hoặc
 * sau {@link QUESTION_SAVE_DELAY_MS} không gõ — không lưu theo từng ô: đi từ
 * tiêu đề sang phương án B không phải hai event trong Lịch sử. Không gửi khi
 * nội dung không đổi (`draftSnapshot`) hoặc chưa hợp lệ (thiếu tiêu đề, phương
 * án chưa có tên): thẻ nói "Chưa lưu" kèm lý do. Lưu đang bay mà người dùng gõ
 * tiếp thì lưu nối một lần nữa khi xong.
 *
 * Câu mới (`question = null`) lưu theo đúng đường ấy; server trả id, và các lần
 * lưu sau là sửa câu đó. Cha giữ thẻ ở cùng `key` khi câu xuất hiện trong sổ,
 * nên ô đang gõ không mất tiêu điểm.
 *
 * ## Bản server đổi
 *
 * Nhận bản mới (refetch 15 s, người viết khác) **chỉ** khi người dùng chưa sửa
 * gì từ bản trước — giống ghi chú của thẻ chế độ Xem.
 *
 * ## Sổ đang `open`
 *
 * Chặn đúng các ca server chặn, ngay trên ô liên quan kèm lý do: phương án
 * đang là đáp án không xoá được (`DECISION_OPTION_IN_USE`); câu đã chốt không
 * xoá được (`DECISION_QUESTION_ANSWERED`). Mã phương án không sửa tay — xoá
 * một phương án không đổi chữ của các phương án còn lại.
 */
export function QuestionCardEditor({
  sheetId,
  projectId,
  status,
  allowOther,
  question,
  newCode,
  newGroupId,
  otherCodes,
  autoFocus = false,
  onCreated,
  onDelete,
}: {
  readonly sheetId: string;
  readonly projectId: string;
  readonly status: DecisionSheetStatus;
  readonly allowOther: boolean;
  /** `null` = câu mới chưa có trên server. */
  readonly question: DecisionQuestionView | null;
  /** Mã gợi ý cho câu mới (`nextDecisionCode`). */
  readonly newCode: string;
  /** Nhóm của câu mới. Câu đã có đổi nhóm bằng kéo thả, không qua đây. */
  readonly newGroupId: string | null;
  /** Mã của mọi câu **khác** trong sổ. */
  readonly otherCodes: readonly string[];
  readonly autoFocus?: boolean;
  readonly onCreated: (questionId: string) => void;
  /** `null` ⇒ bỏ câu mới chưa lưu. */
  readonly onDelete: (questionId: string | null, code: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const articleRef = useRef<HTMLElement>(null);

  const [draft, setDraft] = useState<QuestionDraft>(() =>
    question ? draftOf(question) : emptyDraft(newCode),
  );
  const [touched, setTouched] = useState(false);

  const serverSnap = useMemo(
    () => (question ? draftSnapshot(draftOf(question)) : null),
    [question],
  );
  /** Nội dung server đang giữ (hoặc vừa nhận lệnh lưu) — so với nó để biết có gì cần lưu. */
  const savedSnap = useRef<string | null>(serverSnap);
  const [seenSnap, setSeenSnap] = useState(serverSnap);
  if (serverSnap !== seenSnap) {
    setSeenSnap(serverSnap);
    if (question && seenSnap !== null && draftSnapshot(draft) === seenSnap) {
      setDraft(draftOf(question, draft));
    }
  }
  useEffect(() => {
    if (serverSnap !== null) savedSnap.current = serverSnap;
  }, [serverSnap]);

  const createdId = useRef<string | null>(null);
  const questionId = question?.id ?? createdId.current ?? undefined;

  const inputOf = (current: QuestionDraft) =>
    questionInputOf(
      current,
      // Câu đã có: không gửi `groupId` — nhóm đổi bằng kéo thả.
      questionId ? { questionId } : { groupId: newGroupId },
      otherCodes,
      (code) => t('decisionSheet.editor.duplicateCode', { code }),
    );
  const parsed = inputOf(draft);
  const dirty = draftSnapshot(draft) !== (savedSnap.current ?? '');

  const inFlight = useRef(false);
  const again = useRef(false);
  const latest = useRef<() => void>(() => {});

  const upsert = useMutation({
    mutationFn: (variables: {
      readonly input: UpsertDecisionQuestionInput;
      readonly snap: string;
    }) => decisionSheetApi.upsertQuestion(sheetId, variables.input),
    onSuccess: (result, variables) => {
      savedSnap.current = variables.snap;
      if (!variables.input.questionId && createdId.current === null) {
        createdId.current = result.id;
        onCreated(result.id);
      }
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', sheetId] });
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(projectId) });
    },
    onSettled: () => {
      inFlight.current = false;
      if (again.current) {
        again.current = false;
        latest.current();
      }
    },
  });

  const save = (): void => {
    const result = inputOf(draft);
    if (!result.ok) return;
    const snap = draftSnapshot(draft);
    if (snap === savedSnap.current) return;
    if (inFlight.current) {
      again.current = true;
      return;
    }
    inFlight.current = true;
    upsert.mutate({ input: result.input, snap });
  };
  latest.current = save;

  // Ngừng gõ một lúc ⇒ lưu, kể cả khi focus còn trong thẻ.
  useEffect(() => {
    const timer = setTimeout(() => latest.current(), QUESTION_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  /** Focus rời **thẻ** (không phải rời một ô sang ô bên cạnh) ⇒ lưu. */
  const onCardBlur = (event: FocusEvent<HTMLElement>): void => {
    const next = event.relatedTarget as Node | null;
    if (next && articleRef.current?.contains(next)) return;
    setTouched(true);
    save();
  };

  /*
    Ô vừa thêm (phương án, dòng "Cần chốt") — tìm theo `data-focus-id` sau khi
    vẽ, như checklist của task: giữ id chứ không giữ chỉ số.
  */
  const focusId = useRef<string | null>(autoFocus ? 'title' : null);
  useEffect(() => {
    const target = focusId.current;
    if (target === null) return;
    focusId.current = null;
    const element = articleRef.current?.querySelector<HTMLTextAreaElement>(
      `[data-focus-id="${target}"]`,
    );
    if (!element) return;
    element.focus();
    element.setSelectionRange(element.value.length, element.value.length);
  }, [draft]);

  const set = (patch: Partial<QuestionDraft>): void => setDraft((d) => ({ ...d, ...patch }));
  const setOption = (rowId: string, patch: { title?: string; detail?: string }): void =>
    setDraft((d) => ({
      ...d,
      options: d.options.map((option) =>
        option.rowId === rowId ? { ...option, ...patch } : option,
      ),
    }));
  const setAsk = (rowId: string, text: string): void =>
    setDraft((d) => ({
      ...d,
      asks: d.asks.map((ask) => (ask.rowId === rowId ? { ...ask, text } : ask)),
    }));

  const addAskAfter = (afterRowId: string | null): void => {
    const next = insertAsk(draft, afterRowId);
    if (!next) return;
    focusId.current = next.rowId;
    setDraft(next.draft);
  };
  const askKeyDown = (rowId: string, text: string) => (event: KeyboardEvent) => {
    if (event.key === 'Backspace' && text === '') {
      event.preventDefault();
      const next = removeAsk(draft, rowId);
      focusId.current = next.focus;
      setDraft(next.draft);
    }
  };
  const addOptionRow = (): void => {
    const next = addOption(draft);
    if (!next) return;
    focusId.current = next.rowId;
    setDraft(next.draft);
  };

  const answer = question?.answer ?? null;
  // Phương án đang là đáp án thì không xoá được — câu chọn nhiều là **từng** mã.
  const lockedKeys = new Set(
    status === 'open' && answer
      ? splitDecisionKeys(answer.key).filter((key) => key !== DECISION_OTHER_KEY)
      : [],
  );
  // Đang chốt nhiều mã ⇒ không tắt "chọn nhiều" được (server cũng chặn).
  const multipleLocked = draft.multiple && splitDecisionKeys(answer?.key).length > 1;
  const answeredLock = status === 'open' && answer !== null;
  const issues: DecisionImportIssue[] = parsed.ok ? [] : parsed.issues;
  const showIssues = touched && !parsed.ok;
  const invalidPath = (path: string): boolean | undefined =>
    showIssues && issues.some((issue) => issue.path === path) ? true : undefined;
  const code = draft.code.trim() || newCode;
  const persisted = questionId !== undefined;

  return (
    <article
      ref={articleRef}
      onBlur={onCardBlur}
      className="flex min-w-0 flex-1 flex-col gap-3 rounded-DEFAULT border border-border bg-card p-4 max-sm:p-3"
      data-testid={`decision-question-editor-${persisted ? code : 'new'}`}
    >
      <header className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <CodeChip
          value={draft.code}
          invalid={invalidPath('code')}
          onChange={(value) => set({ code: value })}
        />
        <div className="min-w-0 flex-1 basis-48">
          <InlineText
            data-focus-id="title"
            aria-label={t('decisionSheet.editor.questionTitle')}
            placeholder={t('decisionSheet.editor.questionTitlePlaceholder')}
            value={draft.title}
            maxLength={500}
            aria-invalid={invalidPath('title')}
            onValueChange={(title) => set({ title })}
            className="text-base font-semibold leading-6"
          />
        </div>
        <span className="flex flex-wrap items-center gap-1">
          <span
            role="group"
            aria-label={t('decisionSheet.editor.origin')}
            className="flex flex-wrap gap-1"
          >
            {ORIGINS.map((origin) => {
              const on = draft.origin === origin;
              return (
                <button
                  key={origin}
                  type="button"
                  aria-pressed={on}
                  title={t(`decisionSheet.originHint.${origin}`)}
                  onClick={() => set({ origin: on ? '' : origin })}
                  className={cn(
                    PILL,
                    'relative border transition-colors after:absolute after:-inset-2 after:content-[""]',
                    on
                      ? cn(ORIGIN_CLASS[origin], 'border-transparent')
                      : 'border-dashed border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`decisionSheet.origin.${origin}`)}
                </button>
              );
            })}
          </span>
          <SaveState
            pending={upsert.isPending}
            failed={upsert.isError && !upsert.isPending}
            error={upsert.error}
            saved={upsert.isSuccess && !dirty}
            unsaved={showIssues}
            onRetry={save}
          />
          <button
            type="button"
            disabled={answeredLock}
            aria-label={
              persisted
                ? t('decisionSheet.editor.deleteQuestionCode', { code })
                : t('decisionSheet.editor.discardDraft')
            }
            title={
              answeredLock
                ? t('errors.DECISION_QUESTION_ANSWERED')
                : persisted
                  ? t('decisionSheet.editor.deleteQuestion')
                  : t('decisionSheet.editor.discardDraft')
            }
            onClick={() => onDelete(questionId ?? null, code)}
            className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted-foreground max-sm:size-10"
          >
            {answeredLock ? (
              <Lock aria-hidden className="size-4" />
            ) : (
              <Trash2 aria-hidden className="size-4" />
            )}
          </button>
        </span>
      </header>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t('decisionSheet.sourceRef')}</dt>
        <dd className="min-w-0">
          <InlineText
            multiline
            aria-label={t('decisionSheet.sourceRef')}
            placeholder={t('decisionSheet.editor.sourceRefHint')}
            value={draft.sourceRef}
            maxLength={5_000}
            onValueChange={(sourceRef) => set({ sourceRef })}
          />
        </dd>
        <dt className="text-muted-foreground">{t('decisionSheet.context')}</dt>
        <dd className="min-w-0">
          <InlineText
            multiline
            aria-label={t('decisionSheet.context')}
            placeholder={t('decisionSheet.editor.contextHint')}
            value={draft.context}
            maxLength={5_000}
            onValueChange={(context) => set({ context })}
          />
        </dd>
      </dl>

      <div className="text-sm">
        <p className="font-medium">{t('decisionSheet.asks')}</p>
        {draft.asks.length > 0 ? (
          <ol className="ml-5 list-decimal space-y-0.5">
            {draft.asks.map((ask, index) => (
              <li key={ask.rowId} className="pl-1">
                <InlineText
                  data-focus-id={ask.rowId}
                  aria-label={t('decisionSheet.editor.askLine', { index: index + 1 })}
                  placeholder={t('decisionSheet.editor.askHint')}
                  value={ask.text}
                  maxLength={500}
                  onValueChange={(text) => setAsk(ask.rowId, text)}
                  onEnter={() => addAskAfter(ask.rowId)}
                  onKeyDown={askKeyDown(ask.rowId, ask.text)}
                />
              </li>
            ))}
          </ol>
        ) : null}
        {draft.asks.length < MAX_DECISION_ASKS ? (
          <button
            type="button"
            onClick={() => addAskAfter(null)}
            className="mt-0.5 inline-flex h-8 items-center gap-1 rounded-full px-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground max-sm:h-10"
          >
            <Plus aria-hidden className="size-3.5" />
            {t('decisionSheet.editor.addAsk')}
          </button>
        ) : null}
      </div>

      {/* Chọn một ↔ chọn nhiều — APM-DSX-multi. Bật thì xoá mọi "nên chọn". */}
      <label
        className="flex w-max max-w-full items-center gap-2 text-sm"
        title={multipleLocked ? t('decisionSheet.editor.multipleLocked') : undefined}
      >
        <input
          type="checkbox"
          className="ios-switch"
          checked={draft.multiple}
          disabled={multipleLocked}
          onChange={() => setDraft((d) => toggleMultiple(d))}
          data-testid="decision-multiple-toggle"
        />
        <span>{t('decisionSheet.editor.multiple')}</span>
      </label>

      <div
        role="list"
        aria-label={t('decisionSheet.options', { code })}
        className="flex flex-col gap-2"
      >
        {draft.options.map((option, index) => {
          const inUse = lockedKeys.has(option.key);
          const tooFew = draft.options.length <= MIN_DECISION_OPTIONS;
          return (
            <div
              key={option.rowId}
              role="listitem"
              data-testid={`decision-option-editor-${option.key}`}
              className={cn(
                'group/option flex min-w-0 items-start gap-3 rounded-DEFAULT border px-3 py-2 text-sm transition-colors',
                option.recommended
                  ? 'border-rag-green/50 bg-rag-green/5'
                  : 'border-border bg-background',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'mt-1 size-4 shrink-0 border-2 border-border-strong',
                  draft.multiple ? 'rounded-[5px]' : 'rounded-full',
                )}
              />
              <span className="w-5 shrink-0 py-0.5 font-mono font-semibold">{option.key}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex flex-wrap items-start gap-x-2 gap-y-0.5">
                  <span className="min-w-0 flex-1 basis-32">
                    <InlineText
                      data-focus-id={option.rowId}
                      aria-label={t('decisionSheet.editor.optionTitle', { key: option.key })}
                      placeholder={t('decisionSheet.editor.optionTitlePlaceholder')}
                      value={option.title}
                      maxLength={300}
                      aria-invalid={invalidPath(`options[${index}].title`)}
                      onValueChange={(title) => setOption(option.rowId, { title })}
                      className="font-medium"
                    />
                  </span>
                  {draft.multiple ? null : (
                  <button
                    type="button"
                    aria-pressed={option.recommended}
                    aria-label={t('decisionSheet.editor.recommendToggle', { key: option.key })}
                    title={t('decisionSheet.recommendedHint')}
                    onClick={() => setDraft((d) => toggleRecommended(d, option.rowId))}
                    className={cn(
                      PILL,
                      'relative mt-0.5 gap-1 border transition-[color,background-color,opacity] after:absolute after:-inset-2 after:content-[""]',
                      option.recommended
                        ? 'border-transparent bg-rag-green/15 text-rag-green'
                        : cn(
                            'border-dashed border-border text-muted-foreground hover:text-rag-green',
                            HOVER_REVEAL,
                          ),
                    )}
                  >
                    <Star
                      aria-hidden
                      className={cn('size-3', option.recommended && 'fill-current')}
                    />
                    {t('decisionSheet.recommended')}
                  </button>
                  )}
                </span>
                <InlineText
                  aria-label={t('decisionSheet.editor.optionDetail', { key: option.key })}
                  placeholder={t('decisionSheet.editor.optionDetailHint')}
                  value={option.detail}
                  maxLength={2_000}
                  onValueChange={(detail) => setOption(option.rowId, { detail })}
                  className="text-muted-foreground"
                />
                {inUse ? (
                  <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock aria-hidden className="size-3" />
                    {t('decisionSheet.editor.optionInUse')}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                disabled={inUse || tooFew}
                aria-label={t('decisionSheet.editor.removeOption', { key: option.key })}
                title={
                  inUse
                    ? t('errors.DECISION_OPTION_IN_USE')
                    : tooFew
                      ? t('decisionSheet.editor.minOptions', { min: MIN_DECISION_OPTIONS })
                      : t('decisionSheet.editor.removeOption', { key: option.key })
                }
                onClick={() =>
                  set({ options: draft.options.filter((entry) => entry.rowId !== option.rowId) })
                }
                className={cn(
                  'inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-[color,background-color,opacity] hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground max-sm:size-10',
                  inUse || tooFew
                    ? 'opacity-0 group-hover/option:opacity-40 max-sm:opacity-40 pointer-coarse:opacity-40'
                    : HOVER_REVEAL,
                )}
              >
                <X aria-hidden className="size-4" />
              </button>
            </div>
          );
        })}

        {draft.options.length < MAX_DECISION_OPTIONS ? (
          <button
            type="button"
            onClick={addOptionRow}
            className="flex min-h-10 items-center gap-2 rounded-DEFAULT border border-dashed border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
          >
            <Plus aria-hidden className="size-4" />
            {t('decisionSheet.editor.addOption')}
          </button>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('decisionSheet.editor.optionLimit', { max: MAX_DECISION_OPTIONS })}
          </p>
        )}

        {allowOther ? (
          <div
            className="flex min-w-0 items-start gap-3 rounded-DEFAULT border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
            title={t('decisionSheet.editor.otherPreview')}
          >
            <span
              aria-hidden
              className={cn(
                'mt-1 size-4 shrink-0 border-2 border-border',
                draft.multiple ? 'rounded-[5px]' : 'rounded-full',
              )}
            />
            <span className="w-5 shrink-0 font-mono font-semibold">{DECISION_OTHER_KEY}</span>
            <span className="flex min-w-0 flex-col">
              <span className="font-semibold">{t('decisionSheet.other')}</span>
              <span>{t('decisionSheet.editor.otherPreview')}</span>
            </span>
          </div>
        ) : null}
      </div>

      {answeredLock && answer ? (
        <p
          role="note"
          className="flex items-start gap-1.5 rounded-DEFAULT bg-muted/60 px-3 py-2 text-xs text-muted-foreground"
        >
          <Lock aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          {t('decisionSheet.editor.answeredLock', { key: answer.key })}
        </p>
      ) : null}

      {showIssues ? (
        <ul
          role="alert"
          data-testid="decision-question-issues"
          className="flex flex-col gap-0.5 text-xs text-destructive"
        >
          {issues.map((issue, index) => (
            <li key={`${issue.path}-${index}`} className="flex items-start gap-1.5">
              <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
              {issueText(issue, draft, t)}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

/** Lỗi validate ⇒ một câu người đọc hiểu (thay vì `options[1].title: Too small`). */
function issueText(issue: DecisionImportIssue, draft: QuestionDraft, t: TFunction): string {
  if (issue.path === 'title') return t('decisionSheet.editor.needTitle');
  if (issue.path === 'code' && draft.code.trim() === '') return t('decisionSheet.editor.needCode');
  const option = issue.path.match(/^options\[(\d+)\]\.title$/);
  if (option) {
    const key = draft.options[Number(option[1])]?.key ?? '?';
    return t('decisionSheet.editor.needOptionTitle', { key });
  }
  return issue.message;
}

const CHIP_TONE = {
  /** Mã câu. */
  muted: 'bg-muted text-muted-foreground',
  /** Mã nhóm — nền đậm như chế độ Xem. */
  solid: 'bg-foreground font-semibold text-background',
} as const;

/**
 * Mã câu / mã nhóm — nhãn mono như chế độ Xem; bấm vào thì thành ô sửa. Vùng bấm nới ra
 * 8px mỗi phía (`after:`) để điện thoại nhắm được một nhãn cao 20px.
 */
export function CodeChip({
  value,
  invalid,
  onChange,
  tone = 'muted',
  label,
  editLabel,
}: {
  readonly value: string;
  readonly invalid: boolean | undefined;
  readonly onChange: (value: string) => void;
  readonly tone?: keyof typeof CHIP_TONE;
  /** Nhãn của ô khi đang sửa — mặc định "Mã câu". */
  readonly label?: string;
  /** Nhãn của nút — mặc định "Mã câu … — bấm để sửa". */
  readonly editLabel?: string;
}): ReactNode {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <input
        autoFocus
        aria-label={label ?? t('decisionSheet.editor.code')}
        value={value}
        maxLength={20}
        aria-invalid={invalid}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === 'Escape') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        className="h-6 w-24 shrink-0 border border-primary bg-background px-2 font-mono text-xs focus:outline-none aria-invalid:border-destructive"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={editLabel ?? t('decisionSheet.editor.codeEdit', { code: value })}
      title={editLabel ?? t('decisionSheet.editor.codeEdit', { code: value })}
      className={cn(
        'relative shrink-0 rounded-sm px-1.5 font-mono text-xs leading-5 transition-shadow after:absolute after:-inset-2 after:content-[""] hover:ring-1 hover:ring-border-strong',
        CHIP_TONE[tone],
        invalid && 'ring-1 ring-destructive',
      )}
    >
      {value.trim() === '' ? '—' : value}
    </button>
  );
}

/** "Đang lưu… / Đã lưu / Lỗi — thử lại / Chưa lưu" của một câu. */
export function SaveState({
  pending,
  failed,
  error,
  saved,
  unsaved,
  onRetry,
}: {
  readonly pending: boolean;
  readonly failed: boolean;
  readonly error: unknown;
  readonly saved: boolean;
  readonly unsaved: boolean;
  readonly onRetry: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center text-xs">
      {pending ? (
        <span className="px-1 text-muted-foreground">{t('decisionSheet.editor.saving')}</span>
      ) : failed ? (
        <button
          type="button"
          onClick={onRetry}
          title={errorMessage(error, t)}
          className="inline-flex h-8 items-center gap-1 rounded-full px-2 text-destructive hover:bg-destructive/10 max-sm:h-10"
        >
          <TriangleAlert aria-hidden className="size-3.5" />
          {t('decisionSheet.editor.saveFailed')}
        </button>
      ) : unsaved ? (
        <span className="px-1 text-rag-amber">{t('decisionSheet.editor.unsaved')}</span>
      ) : saved ? (
        <span className="inline-flex items-center gap-1 px-1 text-rag-green">
          <Check aria-hidden className="size-3.5" />
          {t('decisionSheet.editor.saved')}
        </span>
      ) : null}
    </span>
  );
}
